import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";

const EASE = [0.25, 1, 0.5, 1];

const lineWrap = {
  hidden: {},
  show: { transition: { staggerChildren: 0.15, delayChildren: 0.4 } },
};
const line = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1.1, ease: EASE } },
};

const heroBg =
  "https://images.pexels.com/photos/10514509/pexels-photo-10514509.jpeg";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  const lines = ["A World", "Curated", "Without Limits."];

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-ivory"
    >
      {/* Background image with parallax */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 -z-0"
      >
        <img
          src={heroBg}
          alt="Luxury yacht cruising blue waters"
          className="w-full h-[120%] object-cover"
        />
      </motion.div>
      {/* Ivory wash overlay */}
      <div className="absolute inset-0 bg-ivory/72 -z-0" />
      <div className="absolute inset-0 bg-gradient-to-t from-ivory via-ivory/10 to-transparent -z-0" />
      <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-ivory/85 to-transparent -z-0" />

      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 min-h-screen flex flex-col justify-center"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="mb-8 mt-20 md:mt-24"
        >
          <span className="text-xs tracking-[0.32em] uppercase text-gold font-sans">
            Invitation Only · Est. Worldwide
          </span>
        </motion.div>

        <motion.h1
          variants={lineWrap}
          initial="hidden"
          animate="show"
          data-testid="hero-heading"
          className="font-serif font-light text-navy leading-[0.92] tracking-tight text-6xl sm:text-7xl md:text-8xl lg:text-[8.5rem]"
        >
          {lines.map((l, i) => (
            <span key={i} className="block overflow-hidden py-1">
              <motion.span variants={line} className="block">
                {i === 2 ? (
                  <span className="italic text-gold">{l}</span>
                ) : (
                  l
                )}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 1.1 }}
          className="mt-10 max-w-xl text-base md:text-lg font-sans font-light text-navyAccent leading-relaxed"
        >
          A private lifestyle house serving a discreet circle of members. We
          procure the unattainable, open closed doors, and orchestrate the
          extraordinary — anywhere on earth.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 1.3 }}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <a
            href="#membership"
            data-testid="hero-request-btn"
            className="group inline-flex items-center gap-3 rounded-full bg-navy px-8 py-4 text-xs uppercase tracking-[0.18em] text-ivory hover:bg-navyAccent transition-colors duration-300"
          >
            Request an Invitation
            <span className="h-1.5 w-1.5 rounded-full bg-gold transition-transform duration-300 group-hover:scale-150" />
          </a>
          <a
            href="#services"
            data-testid="hero-explore-btn"
            className="inline-flex items-center rounded-full border border-navy/30 px-8 py-4 text-xs uppercase tracking-[0.18em] text-navy hover:border-gold hover:text-gold transition-colors duration-300"
          >
            Explore the House
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-12 z-10 flex items-center gap-3 text-navyAccent"
      >
        <span className="text-[0.65rem] tracking-[0.25em] uppercase font-sans">
          Scroll
        </span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <ArrowDown size={16} className="text-gold" />
        </motion.span>
      </motion.div>
    </section>
  );
};
